import { useParticipants } from '@livekit/components-react'
import { useEffect, useState } from 'react'

import { t } from './i18n'
import type { ConnectData } from './types'
import mark from './assets/ischool-mark.svg'

/**
 * Branded bar above the conference grid: who they are, which class, and — the
 * part that is not decoration — a persistent recording indicator.
 *
 * Every class here is recorded, and these are children. A visible, always-on
 * indicator is the honest way to represent that, rather than burying it in a
 * notice on the screen before.
 */
export function RoomHeader({
  context,
  onFallback,
  anyBad = false,
  onOpenSetupHub,
}: {
  context: ConnectData
  /**
   * Host-only: opens the "move this class to Zoom" confirmation. It lives up
   * HERE, quiet, top corner — a last resort must be findable, not prominent;
   * in the control bar it sat lit up beside Celebrate and read as a feature.
   */
  onFallback?: () => void
  anyBad?: boolean
  onOpenSetupHub?: () => void
}) {
  const [elapsed, setElapsed] = useState('')

  // Elapsed time rather than a countdown: classes overrun, and a timer counting
  // down to zero and then going negative reads like something is wrong.
  useEffect(() => {
    const startedAt = Date.now()
    const tick = () => {
      const secs = Math.floor((Date.now() - startedAt) / 1000)
      const mm = String(Math.floor(secs / 60)).padStart(2, '0')
      const ss = String(secs % 60).padStart(2, '0')
      setElapsed(`${mm}:${ss}`)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  // Reactive, not room.remoteParticipants.size: the raw read only refreshed
  // when the 1s clock happened to re-render — a join could lag the badge.
  const participants = useParticipants().length

  return (
    <header className="room-header">
      <div className="room-header__brand">
        <img src={mark} alt="iSchool" height={28} />
        <span className="room-header__class">
          {context.session.type === 'group' ? t('prejoin.group') : t('prejoin.solo')}
        </span>
        {anyBad ? <button type="button" className="room-header__help-dot" onClick={onOpenSetupHub} aria-label={t('help.header')} title={t('help.header')} /> : null}
      </div>

      <div className="room-header__meta">
        {/* Shown ONLY to the observer themselves: their token is hidden from
            everyone else, so this pill exists to reassure THIS viewer that they
            are present but invisible — nobody else's header carries it. */}
        {context.current_user.meetingRole === 'Ghost' ? (
          <span className="pill pill--observing">
            <EyeIcon />
            {t('room.observing')}
          </span>
        ) : null}
        <span className="pill pill--rec">
          <span className="pill__dot" aria-hidden="true" />
          {t('room.recording')}
        </span>
        {/* pill--time reserves the digits' width — a timer that reflows every
            second reads as flicker. */}
        <span className="pill pill--time">{elapsed}</span>
        <span className="pill">
          <PeopleIcon />
          {participants}
        </span>
        {onFallback ? (
          <button
            type="button"
            className="room-header__fallback"
            onClick={onFallback}
            aria-label={t('fallback.controlHint')}
            title={t('fallback.controlHint')}
          >
            <LifebuoyIcon />
            {t('fallback.headerLabel')}
          </button>
        ) : null}
      </div>
    </header>
  )
}

const LifebuoyIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 5a5 5 0 0 0-2.4.62L7.4 5.4A8 8 0 0 1 12 4a8 8 0 0 1 4.6 1.4l-2.2 2.22A5 5 0 0 0 12 7Zm-6.6.4 2.22 2.2A5 5 0 0 0 7 12c0 .87.22 1.68.62 2.4L5.4 16.6A8 8 0 0 1 4 12a8 8 0 0 1 1.4-4.6Zm11 1.2 2.2-2.2A8 8 0 0 1 20 12a8 8 0 0 1-1.4 4.6l-2.22-2.2c.4-.72.62-1.53.62-2.4a5 5 0 0 0-.62-2.4ZM12 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm-4.6 3.6 2.2-2.22c.72.4 1.53.62 2.4.62s1.68-.22 2.4-.62l2.2 2.22A8 8 0 0 1 12 20a8 8 0 0 1-4.6-1.4Z" />
  </svg>
)

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 2c-2.3 0-6 1.2-6 3.5V19h12v-2.5C14 14.2 10.3 13 8 13Zm8 0c-.7 0-1.5.1-2.3.3 1.4.9 2.3 2 2.3 3.2V19h6v-2.5c0-2.3-3.7-3.5-6-3.5Z" />
  </svg>
)

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M12 5c-5 0-9 4.5-10 7 1 2.5 5 7 10 7s9-4.5 10-7c-1-2.5-5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
  </svg>
)
